import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { confirmPasswordReset } from '../utils/apiServices';

function ResetPassword() {
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Comprobación de contraseña en vivo (mismas reglas que el registro y el backend).
  const passwordRequirements = [
    { key: 'length', label: 'Mínimo 8 caracteres', valid: password.length >= 8 },
    { key: 'uppercase', label: 'Una letra mayúscula', valid: /[A-Z]/.test(password) },
    { key: 'number', label: 'Un número', valid: /[0-9]/.test(password) },
    { key: 'symbol', label: 'Un símbolo (!@#$…)', valid: /[^A-Za-z0-9]/.test(password) },
  ];
  const passwordScore = passwordRequirements.filter((req) => req.valid).length;
  const passwordStrength = [
    { label: '', bar: 'w-0 bg-transparent', text: 'text-texto-sec' },
    { label: 'Muy débil', bar: 'w-1/4 bg-red-500', text: 'text-red-600' },
    { label: 'Débil', bar: 'w-2/4 bg-orange-500', text: 'text-orange-700' },
    { label: 'Media', bar: 'w-3/4 bg-yellow-500', text: 'text-yellow-700' },
    { label: 'Fuerte', bar: 'w-full bg-green-500', text: 'text-green-700' },
  ][passwordScore];
  const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword;

  const handleSubmit = async (event) => {
    event.preventDefault();

    const meetsRequirements =
      password.length >= 8 &&
      /[A-Z]/.test(password) &&
      /[0-9]/.test(password) &&
      /[^A-Za-z0-9]/.test(password);

    if (!meetsRequirements) {
      setErrors('La contraseña no cumple los requisitos de seguridad.');
      setMessage('');
      return;
    }
    if (password !== confirmPassword) {
      setErrors('Las contraseñas no coinciden.');
      setMessage('');
      return;
    }

    setSubmitting(true);
    try {
      await confirmPasswordReset(uid, token, password);
      setErrors('');
      setMessage('Tu contraseña fue restablecida exitosamente. Te llevaremos al inicio de sesión.');
      setTimeout(() => navigate('/login'), 2500);
    } catch (error) {
      setErrors(error.response?.data?.detail || 'El enlace es inválido o expiró. Solicita uno nuevo.');
      setMessage('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f4f7fc] to-[#e8f0fe] py-[40px] px-5 max-md:py-5 max-md:px-[15px]">
      <div className="w-full max-w-[600px] animate-fade-in">
        <div className="bg-white text-center rounded-[30px] py-[60px] px-[50px] shadow-[0_20px_60px_rgba(0,63,129,0.15)] max-md:py-[40px] max-md:px-[30px] max-md:rounded-[20px]">
          <div className="flex items-center justify-center mx-auto w-[100px] h-[100px] mb-[30px] bg-gradient-to-br from-icetex to-[#0056b3] rounded-full shadow-[0_10px_30px_rgba(0,63,129,0.3)] max-md:w-[80px] max-md:h-[80px]">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-[50px] h-[50px] text-white max-md:w-[40px] max-md:h-[40px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
          </div>

          <h1 className="text-texto-oscuro font-extrabold text-[2.2rem] mb-[15px] max-md:text-[1.8rem]">Restablece tu Contraseña</h1>
          <p className="text-texto-sec text-[1.05rem] leading-[1.6] mb-[40px] max-md:text-base">
            Crea una nueva contraseña para tu cuenta
          </p>

          <form onSubmit={handleSubmit} className="text-left">
            <div className="mb-[25px]">
              <label htmlFor="password" className="flex items-center text-texto-oscuro font-bold uppercase gap-2 text-[0.95rem] mb-[10px] tracking-[0.5px]">
                <svg xmlns="http://www.w3.org/2000/svg" className="text-icetex w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                Nueva Contraseña
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full outline-none py-4 px-5 border-2 border-[#e8f0fe] rounded-[15px] text-base bg-[#f8fafc] transition-all duration-300 focus:border-icetex focus:bg-white focus:shadow-[0_0_0_4px_rgba(0,63,129,0.1)] placeholder:text-[#94a3b8]"
                placeholder="Ingresa tu nueva contraseña"
              />

              {password.length > 0 && (
                <div className="mt-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-[6px] bg-[#e8f0fe] rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-300 ${passwordStrength.bar}`} />
                    </div>
                    <span className={`text-[0.8rem] font-bold whitespace-nowrap ${passwordStrength.text}`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <ul className="list-none p-0 m-0 mt-3 grid grid-cols-2 gap-x-4 gap-y-[6px] max-md:grid-cols-1">
                    {passwordRequirements.map((req) => (
                      <li key={req.key} className={`flex items-center gap-2 text-[0.82rem] font-semibold ${req.valid ? 'text-green-700' : 'text-texto-sec'}`}>
                        {req.valid ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0 text-[#cbd5e1]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="9"></circle>
                          </svg>
                        )}
                        {req.label}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="mb-[25px]">
              <label htmlFor="confirmPassword" className="flex items-center text-texto-oscuro font-bold uppercase gap-2 text-[0.95rem] mb-[10px] tracking-[0.5px]">
                <svg xmlns="http://www.w3.org/2000/svg" className="text-icetex w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                Confirmar Contraseña
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className={`w-full outline-none py-4 px-5 border-2 rounded-[15px] text-base bg-[#f8fafc] transition-all duration-300 focus:bg-white placeholder:text-[#94a3b8] ${
                  confirmPassword.length === 0
                    ? 'border-[#e8f0fe] focus:border-icetex focus:shadow-[0_0_0_4px_rgba(0,63,129,0.1)]'
                    : passwordsMatch
                      ? 'border-green-400 focus:shadow-[0_0_0_4px_rgba(34,197,94,0.12)]'
                      : 'border-red-400 focus:shadow-[0_0_0_4px_rgba(239,68,68,0.12)]'
                }`}
                placeholder="Vuelve a escribir tu nueva contraseña"
              />
              {confirmPassword.length > 0 && (
                <p className={`flex items-center gap-[6px] m-0 mt-2 text-[0.82rem] font-semibold ${passwordsMatch ? 'text-green-700' : 'text-red-600'}`}>
                  {passwordsMatch ? '✓ Las contraseñas coinciden' : '✗ Las contraseñas no coinciden'}
                </p>
              )}
            </div>

            {errors && (
              <div className="flex items-center gap-3 py-4 px-5 bg-[#fee] border-2 border-[#fcc] rounded-[15px] mb-[25px] animate-shake">
                <svg xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 w-5 h-5 text-[#c00]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <p className="m-0 text-[#c00] text-[0.95rem] font-semibold">{errors}</p>
              </div>
            )}

            {message && (
              <div className="flex items-center gap-3 py-4 px-5 border-2 rounded-[15px] mb-[25px]" style={{ backgroundColor: '#e8f5e9', borderColor: '#c8e6c9' }}>
                <svg xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#4caf50' }}>
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                <p className="m-0 text-[0.95rem] font-semibold" style={{ color: '#2e7d32' }}>{message}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full text-white border-0 font-bold uppercase cursor-pointer py-[18px] bg-gradient-to-br from-icetex to-[#0056b3] rounded-[50px] text-[1.05rem] tracking-[0.5px] shadow-[0_6px_20px_rgba(0,63,129,0.3)] mb-[15px] transition-all duration-300 hover:-translate-y-[0.5px] hover:shadow-[0_8px_25px_rgba(0,63,129,0.4)] active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? 'Guardando...' : 'Restablecer contraseña'}
            </button>

            <Link to="/login" className="w-full bg-transparent text-icetex font-semibold cursor-pointer text-center py-4 border-2 border-icetex rounded-[50px] text-[0.95rem] mb-5 transition-all duration-300 hover:bg-icetex hover:text-white hover:-translate-y-[0.5px] no-underline block">
              Volver a Iniciar Sesión
            </Link>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
